/**
 * ClockSync — estimates this client's offset to the server's clock so that a
 * timestamp minted by one peer means the same instant to the other. Both peers
 * converge to the server timebase, so they share a timebase with each other.
 *
 * Method: NTP four-timestamp offset/RTT per sample, keep a sliding window, and
 * select the offset from the MINIMUM-RTT sample (lowest latency => least
 * queuing => most trustworthy). Ease the live offset toward that best value in
 * bounded steps so a big correction converges over a few updates instead of
 * teleporting.
 *
 * All local timing uses an injected monotonic `now()` (performance.now in the
 * browser), never wall-clock Date.now which jumps on NTP/DST corrections.
 */
import { TIMING } from "../shared/protocol.ts";

export interface ClockSample {
  offsetMs: number;
  rttMs: number;
}

export interface ClockSyncOptions {
  now: () => number;
  window?: number;
  maxStepMs?: number;
}

export class ClockSync {
  private readonly now: () => number;
  private readonly window: number;
  private readonly maxStepMs: number;
  private samples: ClockSample[] = [];
  private offsetMs = 0;
  private bestRttMs = Infinity;
  private initialized = false;

  constructor(opts: ClockSyncOptions) {
    this.now = opts.now;
    this.window = opts.window ?? TIMING.CLOCK_WINDOW;
    this.maxStepMs = opts.maxStepMs ?? TIMING.CLOCK_EMA_MAX_STEP_MS;
  }

  /**
   * Feed one completed round trip.
   * @param cT1 client monotonic ms at ping send
   * @param sT2 server ms at ping receive
   * @param sT3 server ms at pong send
   * @param cT4 client monotonic ms at pong receive
   */
  addSample(cT1: number, sT2: number, sT3: number, cT4: number): ClockSample | null {
    const rttMs = cT4 - cT1 - (sT3 - sT2);
    const offsetMs = (sT2 - cT1 + (sT3 - cT4)) / 2;
    // A negative measured RTT (or any non-finite value) is a corrupt sample —
    // clock-rate mismatch, asymmetry, granularity, or a hostile/buggy peer.
    // Reject it: clamping to 0 would make it the "best" (min-RTT) sample and let
    // the least-trustworthy probe poison the offset and collapse rttHalf.
    if (!Number.isFinite(rttMs) || !Number.isFinite(offsetMs) || rttMs < 0) {
      return null;
    }
    const sample: ClockSample = { offsetMs, rttMs };

    this.samples.push(sample);
    if (this.samples.length > this.window) this.samples.shift();

    // Pick the offset of the lowest-RTT sample currently in the window.
    let best = this.samples[0];
    for (const s of this.samples) if (s.rttMs < best.rttMs) best = s;
    this.bestRttMs = best.rttMs;

    if (!this.initialized) {
      // First sample: adopt immediately so we are usable at once.
      this.offsetMs = best.offsetMs;
      this.initialized = true;
    } else {
      // Ease toward the best offset in bounded steps (anti-teleport).
      const delta = best.offsetMs - this.offsetMs;
      const step = Math.max(-this.maxStepMs, Math.min(this.maxStepMs, delta));
      this.offsetMs += step;
    }
    return sample;
  }

  /** Shared-clock "now" in ms: local monotonic time projected onto server time. */
  sharedNow(): number {
    return this.now() + this.offsetMs;
  }

  /** Current best offset estimate (ms). */
  get offset(): number {
    return this.offsetMs;
  }

  /** One-way latency estimate (ms) — our precision floor for drift thresholds. */
  get rttHalfMs(): number {
    return this.bestRttMs === Infinity ? 0 : this.bestRttMs / 2;
  }

  get ready(): boolean {
    return this.initialized;
  }

  get sampleCount(): number {
    return this.samples.length;
  }
}
